import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, EmptyState, Inline, Panel, Stack, t, Text, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { RefusalPage } from '../../components/Refusal';
import { api, unwrap } from '../../lib/api';
import { clockTime, relativeTime } from '../../lib/format';
import { PendingButton } from '../../components/PendingButton';
import { ListTable } from '../../components/ListTable';

type Integration = maintenance.components['schemas']['Integration'];
type Run = maintenance.components['schemas']['IntegrationRun'];

const STATE_TONES = { connected: 'pos', failing: 'neg', not_configured: 'neutral', unknown: 'warn' } as const;
const OUTCOME_TONES = { running: 'info', success: 'pos', failure: 'neg' } as const;

function detailOf(i: Integration): string {
  const d = i.detail as Record<string, string | number | undefined>;
  if (i.id === 'ovhcloud' && d.servers !== undefined) {
    return t('settings.integrations.detail.ovhcloud', { servers: d.servers, archived: d.archived ?? 0, minutes: d.refreshMinutes ?? 15 });
  }
  if (i.id === 'checkmk' && d.host) return t('settings.integrations.detail.checkmk', { hosts: d.hosts ?? 0, host: d.host });
  if (i.id === 'catalog' && d.servers !== undefined) return t('settings.integrations.detail.catalog', { servers: d.servers });
  if (i.id === 'audit_copy' && d.bucket) return t('settings.integrations.detail.audit_copy', { bucket: d.bucket, behind: d.behind ?? 0 });
  if (i.id === 'siem' && d.destination) return t('settings.integrations.detail.siem', { destination: d.destination, lag: d.lagMinutes ?? 0 });
  if (i.id === 'app_security' && d.components !== undefined) return t('settings.integrations.detail.app_security', { components: d.components });
  return t('settings.integrations.detail.none');
}

/** Réglages › Hébergeurs et intégrations (SPEC-013): sources and OVHcloud synchronisations. */
export function IntegrationsSection() {
  const queryClient = useQueryClient();
  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => unwrap(await api.maintenance.GET('/integrations')).items,
    refetchInterval: 10_000,
  });
  const runs = useQuery({
    queryKey: ['integration-runs', 'ovhcloud'],
    // Whether a run is under way (the latest one); the runs themselves are the list below (D120).
    queryFn: async () => unwrap(await api.maintenance.GET('/integrations/{integration}/runs', { params: { path: { integration: 'ovhcloud' }, query: { pageSize: 1 } } })).items,
    // Faster while a run is in progress.
    refetchInterval: query => (query.state.data?.some(r => r.outcome === 'running') ? 2_000 : 10_000),
  });
  const runNow = useMutation({
    mutationFn: async () => unwrap(await api.maintenance.POST('/integrations/{integration}/run', { params: { path: { integration: 'ovhcloud' } } })),
    onSuccess: () => {
      toast({ title: t('settings.integrations.runRequested'), tone: 'info' });
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['integration-runs'] }), 500);
    },
  });

  if (integrations.error) return <RefusalPage error={integrations.error} />;

  return (
<Stack gap={24}>
      <Panel title={t('settings.integrations.title')} subtitle={t('settings.integrations.subtitle')} flush>
        <ListTable<Integration>
          label={t('settings.integrations.title')}
          queryKey={['integrations', 'list']}
          fetch={async query => unwrap(await api.maintenance.GET('/integrations', { params: { query: query as never } }))}
          rowKey="id"
          defaultSort={{ key: 'id', dir: 'asc' }}
          search={t('settings.integrations.search')}
          facets={[{ key: 'state', label: t('settings.integrations.columns.state'), valueLabel: v => t(`settings.integrations.states.${v}`) }]}
          refetchInterval={10_000}
          columns={[
            { key: 'id', label: t('settings.integrations.columns.integration'), width: 280, render: (i: Integration) => t(`settings.integrations.names.${i.id}`) },
            { key: 'state', label: t('settings.integrations.columns.state'), width: 150, render: (i: Integration) => <Badge tone={STATE_TONES[i.state]}>{t(`settings.integrations.states.${i.state}`)}</Badge> },
            { key: 'lastSuccessAt', label: t('settings.integrations.columns.lastSuccess'), width: 150, render: (i: Integration) => (i.lastSuccessAt ? relativeTime(i.lastSuccessAt) : t('settings.integrations.detail.none')) },
            {
              key: 'detail', label: t('settings.integrations.columns.detail'), width: 240, grow: true, sortable: false,
              // One line: a failing source shows its last error first, then its detail.
              render: (i: Integration) => (i.state === 'failing' && i.lastError && i.lastErrorAt
                ? (
                  <Inline gap={8} wrap={false}>
                    <Text variant="body-sm" tone="neg" truncate>{t('settings.integrations.lastError', { when: clockTime(i.lastErrorAt), error: i.lastError })}</Text>
                    <Text variant="body-sm" tone="mute" truncate>{detailOf(i)}</Text>
                  </Inline>
                )
                : detailOf(i)),
            },
          ]}
          empty={<EmptyState framed={false} icon="hub" title={t('settings.integrations.empty')} />}
        />
      </Panel>
      <Panel
        title={t('settings.runs.title')}
        subtitle={t('settings.runs.subtitle')}
        flush
        actions={integrations.data?.find(i => i.id === 'ovhcloud')?.canRun ? (
          <PendingButton variant="secondary" size="sm" icon="sync" onClick={() => runNow.mutate()} pending={runNow.isPending || Boolean(runs.data?.some(r => r.outcome === 'running'))}>
            {t('settings.integrations.run')}
          </PendingButton>
        ) : undefined}
      >
        <ListTable<Run>
          label={t('settings.runs.title')}
          queryKey={['integration-runs', 'ovhcloud', 'list']}
          fetch={async query => unwrap(await api.maintenance.GET('/integrations/{integration}/runs', { params: { path: { integration: 'ovhcloud' }, query: query as never } }))}
          rowKey={(r: Run) => String(r.id)}
          defaultSort={{ key: 'startedAt', dir: 'desc' }}
          search={t('settings.runs.search')}
          facets={[
            { key: 'outcome', label: t('settings.runs.columns.outcome'), valueLabel: v => t(`settings.runs.outcome.${v}`) },
            { key: 'trigger', label: t('settings.runs.columns.trigger'), valueLabel: v => t(v === 'manual' ? 'settings.runs.triggerManual' : 'settings.runs.trigger.schedule') },
          ]}
          refetchInterval={runs.data?.some(r => r.outcome === 'running') ? 2_000 : 10_000}
          columns={[
            { key: 'startedAt', label: t('settings.runs.columns.startedAt'), width: 180, render: (r: Run) => `${clockTime(r.startedAt)} · ${relativeTime(r.startedAt)}` },
            { key: 'outcome', label: t('settings.runs.columns.outcome'), width: 120, render: (r: Run) => <Badge tone={OUTCOME_TONES[r.outcome]}>{t(`settings.runs.outcome.${r.outcome}`)}</Badge> },
            { key: 'trigger', label: t('settings.runs.columns.trigger'), width: 200, sortable: false, render: (r: Run) => (r.trigger === 'manual' ? t('settings.runs.trigger.manual', { actor: r.actor ?? '—' }) : t('settings.runs.trigger.schedule')) },
            {
              key: 'duration', label: t('settings.runs.columns.duration'), align: 'right', width: 80, sortable: false,
              render: (r: Run) => (r.finishedAt ? t('settings.runs.seconds', { n: Math.max(0, Math.round((Date.parse(r.finishedAt) - Date.parse(r.startedAt)) / 1000)) }) : '—'),
            },
            {
              key: 'detail', label: t('settings.runs.columns.detail'), width: 240, grow: true, sortable: false,
              render: (r: Run) => {
                const summary = r.summary as { active?: number; archived?: number; toReview?: string[] };
                if (r.error) return <Text variant="body-sm" tone="neg" truncate>{r.error}</Text>;
                if (summary.active === undefined) return '—';
                // One line: the servers to review follow the counts.
                return [
                  t('settings.runs.summary', { servers: summary.active, archived: summary.archived ?? 0 }),
                  ...(summary.toReview?.length ? [t('settings.runs.review', { names: summary.toReview.join(', ') })] : []),
                ].join(' · ');
              },
            },
          ]}
          empty={<EmptyState icon="sync" title={t('settings.runs.empty.title')}>{t('settings.runs.empty.body')}</EmptyState>}
        />
      </Panel>
    </Stack>
  );
}
